import { Fragment, useMemo } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import {
  Bars3Icon,
  BellIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  Cog6ToothIcon,
  BoltIcon,
  ChevronDownIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  ShieldExclamationIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '@/store/auth';
import { useLogout } from '@/hooks/useAuth';
import { useCan } from '@/hooks/useEmployeeRights';
import { ThemeToggle } from '@/components/common';
import { GlobalSearch } from '@/components/search';
import { BrokerSwitcher } from './BrokerSwitcher';
import { SETTINGS_ROLES } from '@/pages/settings/settingsAccess';
import { NOTIFICATION_ROLES } from '@/routing/notificationRoles';
import { cn } from '@/utils';
import type { UserRole } from '@/types';

interface HeaderProps {
  onMenuClick: () => void;
}

interface QuickAction {
  label: string;
  to: string;
  icon: React.ElementType;
  /** Roles the destination route actually admits (App.tsx ProtectedRoute). */
  roles: UserRole[];
  /** B4: the module right the destination route requires (`useCan`). */
  allowed: boolean;
}

export function Header({ onMenuClick }: HeaderProps) {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const logoutMutation = useLogout();

  // B4: each action needs Create on its module -- the same cell the route
  // (App.tsx `requires`) and the API (`ModuleRight`) check. Rights gate
  // broker_employee and posp/super_posp; `useCan` passes every other role
  // straight through, so these only ever TIGHTEN the role lists.
  // Hooks must run unconditionally, hence all four up front.
  const canAddCustomer = useCan('contacts', 'create');
  const canAddPolicy = useCan('policies', 'create');
  const canAddQuote = useCan('quotes', 'create');
  const canAddClaim = useCan('claims', 'create');

  /**
   * Quick actions mirror the gates the destination screens already enforce, so
   * the menu can never offer someone an "Access Denied" bounce:
   *
   * - Contact  -- /contacts/new route roles + contacts Create.
   * - Policy   -- /policies/new route roles MINUS posp/super_posp/customer:
 *               (#351: and no insurer roles -- the route refuses them now)
   *               policies are issued by the broker, so PoliciesListPage hides
   *               "+New Policy" from a seller or a buyer even though the route
   *               still admits them. Plus policies Create.
   * - Claim    -- /claims/new route roles + claims Create (BASELINE grants it
   *               to employees, so it only disappears on an explicit deny).
   * - Quote    -- /quotes/new route roles MINUS posp/super_posp (POSP-4 made
   *               POSPs read-only on quotes). Plus quotes Create; insurer roles
   *               are excluded because the route itself rejects them.
   */
  const quickActions = useMemo<QuickAction[]>(
    () => [
      {
        label: 'Create New Contact',
        to: '/contacts/new',
        icon: UserPlusIcon,
        roles: ['broker_admin', 'broker_employee', 'posp', 'super_posp'],
        allowed: canAddCustomer,
      },
      {
        label: 'New Policy',
        to: '/policies/new',
        icon: DocumentTextIcon,
        // #351: insurers do not create policy records (the broker issues
        // them), so the insurer roles are no longer offered this action.
        roles: ['broker_admin', 'broker_employee'],
        allowed: canAddPolicy,
      },
      {
        label: 'New Claim',
        to: '/claims/new',
        icon: ShieldExclamationIcon,
        // #395: insurer roles are read-only on claims (the API refuses create).
        roles: ['broker_admin', 'broker_employee', 'customer'],
        allowed: canAddClaim,
      },
      {
        label: 'New Quote',
        to: '/quotes/new',
        icon: ClipboardDocumentListIcon,
        roles: ['broker_admin', 'broker_employee'],
        allowed: canAddQuote,
      },
    ],
    [canAddCustomer, canAddPolicy, canAddQuote, canAddClaim]
  );

  const availableActions = useMemo(
    () =>
      user
        ? quickActions.filter((action) => action.allowed && action.roles.includes(user.role))
        : [],
    [quickActions, user]
  );

  const handleLogout = () => {
    logoutMutation.mutate();
  };

  /**
   * C1/P1 -- the bug this menu had. The Settings entry below was a BARE
   * `<a href="/settings">` with no role filter at all, while `/settings`
   * admitted only super_admin, country_admin and broker_admin: every customer
   * and every POSP was offered a link straight to Access Denied, which is
   * exactly what the block comment above says this menu must never do.
   *
   * `/settings` now admits them (SettingsPage reads nothing role-specific), and
   * the gate is the SAME imported constant the route uses, so the two cannot
   * drift apart again the way they did here.
   */
  const canOpenSettings = !!user && SETTINGS_ROLES.includes(user.role);
  // #652: the bell opens /notifications, which only some roles may open.
  const canOpenNotifications = !!user && NOTIFICATION_ROLES.includes(user.role);

  return (
    <header className="sticky top-0 z-30 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
      <div className="flex items-center gap-2 h-16 px-4 sm:px-6">
        {/* Left side */}
        <div className="flex items-center shrink-0">
          <button
            onClick={onMenuClick}
            aria-label="Open navigation"
            className="p-2 text-gray-500 hover:text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-700 rounded-lg lg:hidden"
          >
            <Bars3Icon className="w-6 h-6" />
          </button>
        </div>

        {/* Global search -- renders an inline field at sm and up and a
            magnifier + full-screen sheet below it, so the right-hand controls
            keep their space on a narrow screen. */}
        <GlobalSearch />

        {/* Right side. `ml-auto` (not the container's old `justify-between`)
            keeps this group hard right whether or not the search field is
            visible at the current breakpoint. */}
        <div className="flex items-center gap-2 ml-auto shrink-0">
          {/* Broker Switcher */}
          <BrokerSwitcher />

          {/* Theme Toggle */}
          <ThemeToggle />

          {/* Quick Action -- hidden entirely when the role can do none of
              these (super_admin, country_admin, ...) rather than opening onto
              an empty menu. Headless UI's Menu supplies the accessibility
              contract: aria-haspopup/expanded, roving focus with the arrow
              keys, Home/End, type-ahead, Escape and outside-click to close,
              and focus returning to the button on close. */}
          {availableActions.length > 0 && (
            <Menu as="div" className="relative">
              {({ open }) => (
                <>
                  <Menu.Button
                    aria-label="Quick Action"
                    className={cn(
                      'flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
                      'dark:focus-visible:ring-offset-gray-800',
                      open
                        ? 'bg-primary-700 text-white'
                        : 'bg-primary-600 text-white hover:bg-primary-700'
                    )}
                  >
                    <BoltIcon className="w-5 h-5" />
                    <span className="hidden sm:inline">Quick Action</span>
                    <ChevronDownIcon
                      className={cn(
                        'hidden sm:inline w-4 h-4 transition-transform',
                        open && 'rotate-180'
                      )}
                    />
                  </Menu.Button>

                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 mt-2 w-60 origin-top-right bg-white dark:bg-gray-800 rounded-xl shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-700 focus:outline-none p-2 z-40">
                      <p className="px-3 pt-1 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                        Quick Action
                      </p>
                      {availableActions.map((action) => (
                        <Menu.Item key={action.to}>
                          {({ active }) => (
                            <button
                              type="button"
                              onClick={() => navigate(action.to)}
                              className={cn(
                                'flex items-center w-full px-3 py-2 text-sm rounded-lg text-left',
                                active
                                  ? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white'
                                  : 'text-gray-700 dark:text-gray-300'
                              )}
                            >
                              <action.icon className="w-5 h-5 mr-3 text-gray-400 dark:text-gray-500" />
                              {action.label}
                            </button>
                          )}
                        </Menu.Item>
                      ))}
                    </Menu.Items>
                  </Transition>
                </>
              )}
            </Menu>
          )}

          {/* Notifications */}
          {canOpenNotifications && (
            <button
              onClick={() => navigate('/notifications')}
              aria-label="Notifications"
              className="relative p-2 text-gray-500 hover:text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-700 rounded-lg"
            >
              <BellIcon className="w-6 h-6" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
            </button>
          )}

          {/* User menu */}
          <Menu as="div" className="relative">
            <Menu.Button className="flex items-center gap-3 p-1 pr-3 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
              <div className="w-9 h-9 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
                {user?.profile_picture ? (
                  <img
                    src={user.profile_picture}
                    alt={user.full_name}
                    className="w-9 h-9 rounded-full object-cover"
                  />
                ) : (
                  <UserCircleIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                )}
              </div>
              <div className="hidden sm:block text-left">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {user?.full_name || 'User'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                  {user?.role?.replace(/_/g, ' ') || 'Unknown Role'}
                </p>
              </div>
            </Menu.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-700 focus:outline-none divide-y divide-gray-100 dark:divide-gray-700">
                <div className="p-2">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {user?.full_name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{user?.email}</p>
                  </div>
                </div>

                {canOpenSettings && (
                  <div className="p-2">
                    <Menu.Item>
                      {({ active }) => (
                        // `navigate`, not a bare href: this is an in-app route,
                        // and the anchor was reloading the whole SPA (and its
                        // auth bootstrap) on every visit to Settings.
                        <button
                          type="button"
                          onClick={() => navigate('/settings')}
                          className={cn(
                            'flex items-center w-full px-3 py-2 text-sm rounded-lg text-left',
                            active
                              ? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white'
                              : 'text-gray-700 dark:text-gray-300'
                          )}
                        >
                          <Cog6ToothIcon className="w-5 h-5 mr-3 text-gray-400 dark:text-gray-500" />
                          Settings
                        </button>
                      )}
                    </Menu.Item>
                  </div>
                )}

                <div className="p-2">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={handleLogout}
                        disabled={logoutMutation.isPending}
                        className={cn(
                          'flex items-center w-full px-3 py-2 text-sm rounded-lg',
                          active
                            ? 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                            : 'text-gray-700 dark:text-gray-300'
                        )}
                      >
                        <ArrowRightOnRectangleIcon className="w-5 h-5 mr-3 text-gray-400 dark:text-gray-500" />
                        {logoutMutation.isPending ? 'Signing out...' : 'Sign out'}
                      </button>
                    )}
                  </Menu.Item>
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>
    </header>
  );
}
